import { faFacebook as fabFacebook, faInstagram as fabInstagram, faTiktok as fabTiktok, faYoutube as fabYoutube } from '@fortawesome/free-brands-svg-icons';
import { faArrowDown, faChevronDown, faEnvelope, faFacebook, faInstagram, faSearch, faTiktok, faYoutube } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useFetch from "./ApiClient/GetApi";
import FeaturedAccordion from './FeaturedAccordion';
import "./Home.css";
import VideoPlayer from './VideoPlayer/VideoPlayer';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);




// Hero Section Animation Variants
const heroSectionVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.8,
      staggerChildren: 0.2,
      delayChildren: 0.1
    }
  }
};

const featuredArticleVariants = {
  hidden: {
    opacity: 0,
    y: 100,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 1.2,
      ease: [0.25, 0.46, 0.45, 0.94],
      type: "spring",
      stiffness: 100
    }
  }
};

const featuredContentVariants = {
  hidden: {
    opacity: 0,
    y: 50
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      delay: 0.3,
      ease: "easeOut"
    }
  }
};

const headingVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: 0.5,
      ease: "easeOut"
    }
  }
};

const miniNewsRowVariants = {
  hidden: {
    opacity: 0,
    y: 80
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1,
      delay: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const miniNewsCardVariants = {
  hidden: {
    opacity: 0,
    y: 50,
    scale: 0.9
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

const sidebarVariants = {
  hidden: {
    opacity: 0,
    x: 100
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 1,
      delay: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const sidebarTitleVariants = {
  hidden: {
    opacity: 0,
    x: -50
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      delay: 0.8,
      ease: "easeOut"
    }
  }
};

const sidebarListVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 1.0
    }
  }
};

const sidebarItemVariants = {
  hidden: {
    opacity: 0,
    x: 50,
    y: 20
  },
  visible: {
    opacity: 1,
    x: 0,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

// Add this hook at the top of the Home component
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 900);
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
}

export default function Home() {
  const { apiData: homeRes, loading: homeLoading } = useFetch("/user/homeData");
  console.log(homeRes)

  // Refs for GSAP banner animations
  const bannerAdRef = useRef(null);
  const bannerTextRef = useRef(null);
  const bannerBtnRef = useRef(null);
  const bannerAd1Ref = useRef(null);
  const bannerText1Ref = useRef(null);
  const bannerBtn1Ref = useRef(null);

  // Refs for Lunatic Fringe Banner animations
  const lunaticBannerRef = useRef(null);
  const lunaticOverlayRef = useRef(null);
  const lunaticLogoRef = useRef(null);
  const lunaticTitleRef = useRef(null);
  const lunaticDescRef = useRef(null);
  const lunaticBtnRef = useRef(null);

  // GSAP Banner + Lunatic animations (null-safe, SSR-safe)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const banners = [bannerAdRef.current, bannerAd1Ref.current].filter(Boolean);
    const texts = [bannerTextRef.current, bannerText1Ref.current].filter(Boolean);
    const btns = [bannerBtnRef.current, bannerBtn1Ref.current].filter(Boolean);

    const hasLunatic = !!lunaticBannerRef.current;

    if (banners.length) {
      gsap.set(banners, { opacity: 0, y: 100, scale: 0.9, rotationX: 15 });
    }
    if (texts.length) {
      gsap.set(texts, { opacity: 0, y: 60, scale: 0.8 });
    }
    if (btns.length) {
      gsap.set(btns, { opacity: 0, y: 40, scale: 0.7 });
    }

    if (bannerAdRef.current) {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: bannerAdRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
        },
      });
      tl.to(bannerAdRef.current, {
        opacity: 1,
        y: 0,
        scale: 1,
        rotationX: 0,
        duration: 1.2,
        ease: "back.out(1.7)",
      })
        .to(
          bannerTextRef.current,
          { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: "elastic.out(1, 0.3)" },
          "-=0.6",
        )
        .to(
          bannerBtnRef.current,
          { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "back.out(1.7)" },
          "-=0.4",
        );
    }

    if (bannerAd1Ref.current) {
      const tl1 = gsap.timeline({
        scrollTrigger: {
          trigger: bannerAd1Ref.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
        },
      });
      tl1.to(bannerAd1Ref.current, {
        opacity: 1,
        y: 0,
        scale: 1,
        rotationX: 0,
        duration: 1.2,
        ease: "back.out(1.7)",
      })
        .to(
          bannerText1Ref.current,
          { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: "elastic.out(1, 0.3)" },
          "-=0.6",
        )
        .to(
          bannerBtn1Ref.current,
          { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "back.out(1.7)" },
          "-=0.4",
        );
    }

    if (bannerAdRef.current) {
      gsap.to(bannerAdRef.current, {
        yPercent: -10,
        ease: "none",
        scrollTrigger: {
          trigger: bannerAdRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1,
        },
      });
    }
    if (bannerAd1Ref.current) {
      gsap.to(bannerAd1Ref.current, {
        yPercent: -10,
        ease: "none",
        scrollTrigger: {
          trigger: bannerAd1Ref.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1,
        },
      });
    }

    if (texts.length) {
      gsap.to(texts, { y: -5, duration: 2, ease: "power2.inOut", yoyo: true, repeat: -1 });
    }
    if (btns.length) {
      gsap.to(btns, { scale: 1.05, duration: 1.5, ease: "power2.inOut", yoyo: true, repeat: -1 });
    }

    if (hasLunatic) {
      gsap.set(lunaticBannerRef.current, { opacity: 0, y: 100, scale: 0.95 });
      gsap.set(lunaticOverlayRef.current, { opacity: 0, y: 50 });
      gsap.set([lunaticLogoRef.current, lunaticTitleRef.current], { opacity: 0, y: 30, scale: 0.8 });
      gsap.set(lunaticDescRef.current, { opacity: 0, y: 40 });
      gsap.set(lunaticBtnRef.current, { opacity: 0, y: 20, scale: 0.7 });

      const lunaticTl = gsap.timeline({
        scrollTrigger: {
          trigger: lunaticBannerRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse",
        },
      });

      lunaticTl
        .to(lunaticBannerRef.current, { opacity: 1, y: 0, scale: 1, duration: 1.5, ease: "power3.out" })
        .to(lunaticOverlayRef.current, { opacity: 1, y: 0, duration: 1, ease: "power2.out" }, "-=1.2")
        .to(lunaticLogoRef.current, { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: "back.out(1.7)" }, "-=0.8")
        .to(lunaticTitleRef.current, { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: "back.out(1.7)" }, "-=0.6")
        .to(lunaticDescRef.current, { opacity: 1, y: 0, duration: 1, ease: "power2.out" }, "-=0.4")
        .to(lunaticBtnRef.current, { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "back.out(1.7)" }, "-=0.6");

      gsap.to(lunaticBannerRef.current, {
        yPercent: -15,
        ease: "none",
        scrollTrigger: { trigger: lunaticBannerRef.current, start: "top bottom", end: "bottom top", scrub: 1 },
      });

      gsap.to(lunaticLogoRef.current, { y: -8, duration: 3, ease: "power2.inOut", yoyo: true, repeat: -1 });

      if (lunaticDescRef.current?.textContent) {
        const descText = lunaticDescRef.current.textContent;
        lunaticDescRef.current.innerHTML = "";
        descText.split(" ").forEach((word, i) => {
          const span = document.createElement("span");
          span.textContent = word + " ";
          span.style.opacity = "0";
          span.style.transform = "translateY(20px)";
          span.style.display = "inline-block";
          span.style.marginRight = "4px";
          lunaticDescRef.current.appendChild(span);
          gsap.to(span, {
            opacity: 1,
            y: 0,
            duration: 0.4,
            delay: 1.5 + i * 0.05,
            ease: "power2.out",
            scrollTrigger: { trigger: lunaticDescRef.current, start: "top 80%", toggleActions: "play none none reverse" },
          });
        });
      }

      gsap.to(lunaticBtnRef.current, { scale: 1.05, duration: 2, ease: "power2.inOut", yoyo: true, repeat: -1 });
    }

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, [homeLoading]);

  const isMobile = useIsMobile();

  // Helpers
  const timeAgo = (iso) => {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const sec = Math.floor(diff / 1000);
    const min = Math.floor(sec / 60);
    const hr = Math.floor(min / 60);
    const day = Math.floor(hr / 24);
    if (day > 0) return `${day} DAY${day > 1 ? "S" : ""} AGO`;
    if (hr > 0) return `${hr} HOUR${hr > 1 ? "S" : ""} AGO`;
    if (min > 0) return `${min} MIN AGO`;
    return `JUST NOW`;
  };

  const hrefOf = (n) => (n?.id ? `/article/${encodeURIComponent(n.title)}` : "/article");
  const hrefOff = (n) => (n?.id ? `/article/${encodeURIComponent(n.title)}` : "/article");
  const imgOf = (n, fb) => (n?.thumbnail || fb);
  const imgOr = (n, fb) => (n?.thumbnail || fb);

  const excerptFromHtml = (html, limit = 200) => {
    const raw = html ?? "";
    let text = "";
    try {
      if (typeof window !== "undefined" && "DOMParser" in window) {
        text = new DOMParser().parseFromString(String(raw), "text/html").body.textContent || "";
      } else {
        text = String(raw).replace(/<[^>]*>/g, " ");
      }
    } catch {
      text = String(raw).replace(/<[^>]*>/g, " ");
    }
    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length <= limit) return words.join(" ");
    return words.slice(0, limit).join(" ") + "…";
  };

  const tagClassFor = (name = "") => {
    const n = name.toLowerCase();
    if (n.includes("tech")) return "tech";
    if (n.includes("life")) return "lifestyle";
    if (n.includes("shop")) return "shopping";
    if (n.includes("transport")) return "transport";
    if (n.includes("sports")) return "sports";
    if (n.includes("news")) return "news";
    return "news";
  };

  // Fetch-derived memos (keys unchanged)
  const pins = useMemo(() => {
    const arr = homeRes?.data?.pinNews || [];
    const byKey = (k) => arr.find((x) => x.key === k) || null;
    return {
      main: byKey("main"),
      left: byKey("left"),
      right: byKey("right"),
    };
  }, [homeRes]);

  const mainItem = pins.main?.news;
  const leftItem = pins.left?.news;
  const rightItem = pins.right?.news;

  const mainImg = mainItem?.thumbnail || "/placeholder/hero-main.jpg";
  const leftImg = leftItem?.thumbnail || "/placeholder/hero-left.jpg";
  const rightImg = rightItem?.thumbnail || "/placeholder/hero-right.jpg";

  const mainHref = mainItem ? `/article/${encodeURIComponent(mainItem.slug)}` : "/article";
  const leftHref = leftItem ? `/article/${encodeURIComponent(leftItem.slug)}` : "/article";
  const rightHref = rightItem ? `/article/${encodeURIComponent(rightItem.slug)}` : "/article";

  const latestNews = useMemo(() => {
    const arr = homeRes?.data?.latestNews || [];
    return Array.isArray(arr) ? arr : [];
  }, [homeRes]);

  const firstAd = homeRes?.data?.firstAd || null;
  const bannerBg = firstAd?.image || "/4.jpg";
  const bannerText = firstAd?.title || "Bramleys.";

  const secondAd = homeRes?.data?.secondAd || null;
  const secondAdbannerBg = secondAd?.image || "/4.jpg";
  const secondAdbannerText = secondAd?.title || "Bramleys.";

  const trendingByKey = useMemo(() => {
    const arr = homeRes?.data?.trendingNews || [];
    const out = { main: null, left: null, right: null, leftSide: null };
    arr.forEach((row) => {
      if (row?.key && Object.prototype.hasOwnProperty.call(out, row.key)) {
        out[row.key] = row.news || null;
      }
    });
    return out;
  }, [homeRes]);

  const tMain = trendingByKey.main;
  const tLeft = trendingByKey.left;
  const tRight = trendingByKey.right;
  const tLeftSide = trendingByKey.leftSide;

  const dubaiNews = useMemo(() => {
    const arr = homeRes?.data?.dubaiNews || [];
    return Array.isArray(arr) ? arr : [];
  }, [homeRes]);

  const dTall = dubaiNews[0] || null;
  const dMain = dubaiNews[1] || null;
  const dSmall1 = dubaiNews[2] || null;
  const dSmall2 = dubaiNews[3] || null;

  const quikread = useMemo(() => {
    const arr = homeRes?.data?.quikread || [];
    return Array.isArray(arr) ? arr : [];
  }, [homeRes]);

  const techNews = useMemo(() => homeRes?.data?.techNews || [], [homeRes]);
  const shoppingNews = useMemo(() => homeRes?.data?.shoppingNews || [], [homeRes]);
  const lifestyleNews = useMemo(() => homeRes?.data?.lifestyle || [], [homeRes]);

  // Early-return gate (no key changes)
  if (homeLoading || !homeRes) {
    return (
      <div style={{ height: "100vh" }}>
        <div className="quickread-loader">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }


  return (
    <>
      <div className="home-container">
        <main>
          <VideoPlayer url="https://www.youtube.com/watch?v=3VpFFKGCaLQ&list=RD3VpFFKGCaLQ&start_radio=1" />
          {/* HERO SECTION */}
          {isMobile ? (
            <section className="hero-section custom-hero-animate" style={{ opacity: 1, transform: 'none' }}>
              <div className="hero-main">
                {/* MAIN */}
                <div className="featured-article custom-fade-in-left">
                  <img src={mainImg} alt={mainItem?.title || "Featured"} />
                  <div className="featured-content custom-fade-in">
                    <Link
                      to={`/article/${mainItem.slug}`}
                      state={{ category: "news" }}   // yahan actual category bhejo
                      style={{ textDecoration: "none" }}
                    >
                      <span className="tag news">NEWS</span>
                    </Link>


                    <Link to={mainHref} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <h2>{mainItem?.title || "—"}</h2>
                    </Link>
                    {/* Optional teaser if you have it */}
                    {/* <p dangerouslySetInnerHTML={{ __html: (mainItem?.excerpt || "") }} /> */}
                  </div>
                </div>

                {/* LEFT + RIGHT mini cards */}
                <div className="mini-news-row custom-fade-in-right">
                  <div className="mini-news-card">
                    <img src={leftImg} alt={leftItem?.title || "Mini Left"} />
                    <div className="mini-news-content">
                      <Link to={`/article/${leftItem?.slug}`} style={{ textDecoration: 'none' }}>
                        <span className="tag news">NEWS</span>
                      </Link>
                      <Link to={leftHref} style={{ textDecoration: 'none', color: 'inherit' }}>
                        <p>{leftItem?.title || "—"}</p>
                      </Link>
                    </div>
                  </div>

                  <div className="mini-news-card">
                    <img src={rightImg} alt={rightItem?.title || "Mini Right"} />
                    <div>
                      <Link to={`/article/${rightItem?.slug}`} style={{ textDecoration: 'none' }}>
                        <span className="tag update">UPDATE</span>
                      </Link>
                      <Link to={rightHref} style={{ textDecoration: 'none', color: 'inherit' }}>
                        <p>{rightItem?.title || "—"}</p>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <aside className="hero-sidebar custom-slide-in">
                <div className="sidebar-title">
                  LATEST & GREATEST <span className="sidebar-plus"><FontAwesomeIcon icon={faArrowDown} /></span>
                </div>
                <ul className="sidebar-list">
                  {homeLoading ? (
                    <li className="text-muted">Loading…</li>
                  ) : latestNews.length === 0 ? (
                    <li className="text-muted">No latest news yet.</li>
                  ) : (
                    latestNews.map((n) => (
                      <li key={n.id}>
                        <Link to={`/article/${n.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                          <b>{n.title}</b>
                        </Link>
                        <div className="sidebar-list-desc">
                          {timeAgo(n.createdAt)} | {n.user?.name || "—"}
                        </div>
                      </li>
                    ))
                  )}
                </ul>

              </aside>
            </section>
          ) : (
            <motion.section
              className="hero-section custom-hero-animate"
              variants={heroSectionVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <div className="hero-main">
                {/* MAIN */}
                <motion.div
                  className="featured-article custom-fade-in-left"
                  variants={featuredArticleVariants}
                  whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
                >
                  <img src={mainImg} alt={mainItem?.title || "Featured"} />
                  <motion.div className="featured-content custom-fade-in" variants={featuredContentVariants}>
                    <Link to={`/article/${mainItem?.slug}`} style={{ textDecoration: 'none' }}>
                      <span className="tag news">NEWS</span>
                    </Link>
                    <Link to={`/article/${mainItem?.slug}`} style={{ textDecoration: 'none', color: 'white' }}>
                      <motion.h2 variants={headingVariants}>
                        {mainItem?.title || "—"}
                      </motion.h2>
                    </Link>
                  </motion.div>
                </motion.div>

                {/* LEFT + RIGHT mini cards */}
                <motion.div className="mini-news-row custom-fade-in-right" variants={miniNewsRowVariants}>
                  <motion.div
                    className="mini-news-card"
                    variants={miniNewsCardVariants}
                    whileHover={{ y: -5, scale: 1.02, transition: { duration: 0.3 } }}
                  >
                    <img src={leftImg} alt={leftItem?.title || "Mini Left"} />
                    <div className="mini-news-content">
                      <Link to={`/article/${leftItem?.slug}`} style={{ textDecoration: 'none' }}>
                        <span className="tag news">NEWS</span>
                      </Link>
                      <Link to={`/article/${leftItem?.slug}`} style={{ textDecoration: 'none' }}>
                        <p>{leftItem?.title || "—"}</p>
                      </Link>
                    </div>
                  </motion.div>

                  <motion.div
                    className="mini-news-card"
                    variants={miniNewsCardVariants}
                    whileHover={{ y: -5, scale: 1.02, transition: { duration: 0.3 } }}
                  >
                    <img src={leftImg} alt={leftItem?.title || "Mini Right"} />
                    <div>
                      <Link to={`/article/${leftItem?.slug}`} style={{ textDecoration: 'none' }}>
                        <span className="tag update">UPDATE</span>
                      </Link>
                      <Link to={`/article/${rightItem?.slug}`} style={{ textDecoration: 'none' }}>
                        <p>{rightItem?.title || "—"}</p>
                      </Link>
                    </div>
                  </motion.div>
                </motion.div>
              </div>

              <motion.aside
                className="hero-sidebar custom-slide-in"
                variants={sidebarVariants}
                whileHover={{
                  x: -5,
                  transition: { duration: 0.3 }
                }}
              >
                <motion.div
                  className="sidebar-title"
                  variants={sidebarTitleVariants}
                >
                  LATEST & GREATEST <span className="sidebar-plus"><FontAwesomeIcon icon={faArrowDown} /></span>
                </motion.div>
                <motion.ul className="sidebar-list" variants={sidebarListVariants}>
                  {homeLoading ? (
                    <li className="text-muted">Loading…</li>
                  ) : latestNews.length === 0 ? (
                    <li className="text-muted">No latest news yet.</li>
                  ) : (
                    latestNews.map((n) => (
                      <motion.li
                        key={n.id}
                        variants={sidebarItemVariants}
                        whileHover={{ x: 10, transition: { duration: 0.3 } }}
                      >
                        <Link to={`/article/${n.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                          <b>{n.title}</b>
                        </Link>
                        <div className="sidebar-list-desc">
                          {timeAgo(n.createdAt)} | {n.user?.name || "—"}
                        </div>
                      </motion.li>
                    ))
                  )}
                </motion.ul>

              </motion.aside>
            </motion.section>
          )}

          {/* Banner Ad */}
          <div
            ref={bannerAdRef}
            className="banner-ad"
            style={{
              backgroundImage: `url('${bannerBg}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <span ref={bannerTextRef} className="banner-ad-text">
              {bannerText}
            </span>

            <span
              ref={bannerBtnRef}
              className="ad-btn"
              // Optional: click behavior if you later add a destination on the ad object
              onClick={() => {
                // if (firstAd?.url) window.location.href = firstAd.url;
              }}
            >
              SHOP NOW
            </span>
          </div>


          {/* TRENDING SECTION */}
          {isMobile ? (
            <section className="section-block">
              <div className="section-title trending">TRENDING</div>
              <div className="trending-two-col-row">
                {/* LEFT COLUMN */}
                <div className="trending-left-col">
                  {/* Top (main) */}
                  {tMain ? (
                    <div className="trending-large-card">
                      <div className="trending-large-img">
                        <img src={imgOf(tMain, "/5.jpg")} alt={tMain?.title || "Trending"} />
                      </div>
                      <div className="trending-large-content">
                        <Link to={`/article/${tMain?.slug}`} style={{ textDecoration: 'none' }}>
                          <span className="tag news">NEWS</span>
                        </Link>
                        <Link to={`/article/${tMain?.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                          <h3>{tMain?.title || "—"}</h3>
                        </Link>
                        {/* Optional supporting text if you store it */}
                        {/* <p className="supporting">{excerptFromHtml(tMain?.content,50)}</p> */}
                      </div>
                    </div>
                  ) : null}

                  {/* Bottom row: left + leftSide */}
                  <div className="trending-bottom-row-with-border">
                    {tLeft ? (
                      <div className="mini-news-card">
                        <img src={imgOf(tLeft, "/8.jpg")} alt={tLeft?.title || "Trending"} />
                        <div className="trending-small-content">
                          <Link to={`/article/${tLeft?.slug}`} style={{ textDecoration: 'none' }}>
                            <span className="tag news">NEWS</span>
                          </Link>
                          <Link to={`/article/${tLeft?.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <h4>{tLeft?.title || "—"}</h4>
                          </Link>
                        </div>
                      </div>
                    ) : null}

                    {tLeftSide ? (
                      <div className="trending-small-card">
                        <img src={imgOf(tLeftSide, "/7.jpg")} alt={tLeftSide?.title || "Trending"} />
                        <div className="trending-small-content">
                          <Link to={`/article/${tLeftSide?.slug}`} style={{ textDecoration: 'none' }}>
                            <span className="tag news">NEWS</span>
                          </Link>
                          <Link to={`/article/${tLeftSide?.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <h4>{tLeftSide?.title || "—"}</h4>
                          </Link>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* RIGHT COLUMN: right */}
                <div className="trending-right-col">
                  {tRight ? (
                    <div className="trending-tall-card">
                      <img src={imgOf(tRight, "/6.jpg")} alt={tRight?.title || "Trending"} />
                      <div className="trending-tall-overlay">
                        <Link to={`/article/${tRight?.slug}`} style={{ textDecoration: 'none' }}>
                          <span className="tag news">NEWS</span>
                        </Link>
                        <Link to={`/article/${tRight?.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                          <h3>{tRight?.title || "—"}</h3>
                        </Link>
                        {/* <p className="supporting">{tRight?.excerpt}</p> */}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </section>
          ) : (
            <motion.section
              className="section-block"
              variants={heroSectionVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
            >
              <motion.div className="section-title trending" variants={sidebarTitleVariants}>
                TRENDING
              </motion.div>

              <motion.div className="trending-two-col-row" variants={miniNewsRowVariants}>
                {/* LEFT COLUMN */}
                <motion.div className="trending-left-col" variants={sidebarVariants}>
                  {/* Top (main) */}
                  {tMain ? (
                    <motion.div
                      className="trending-large-card"
                      variants={featuredArticleVariants}
                      whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
                    >
                      <motion.div className="trending-large-img" variants={featuredContentVariants}>
                        <img src={imgOf(tMain, "/5.jpg")} alt={tMain?.title || "Trending"} />
                      </motion.div>
                      <motion.div className="trending-large-content" variants={headingVariants}>
                        <Link to={`/article/${tMain?.slug}`} style={{ textDecoration: 'none' }}>
                          <span className="tag news">NEWS</span>
                        </Link>
                        <Link to={`/article/${tMain?.slug}`} style={{ textDecoration: 'none' }}>
                          <motion.h3>
                            {tMain?.title || "—"}
                          </motion.h3>
                        </Link>
                        <p>{excerptFromHtml(tMain?.content, 50)}</p>
                      </motion.div>
                    </motion.div>
                  ) : null}

                  {/* Bottom row: left + leftSide */}
                  <motion.div className="trending-bottom-row-with-border" variants={miniNewsRowVariants}>
                    {tLeft ? (
                      <motion.div
                        className="mini-news-card"
                        variants={miniNewsCardVariants}
                        whileHover={{ y: 0, scale: 1.02, transition: { duration: 0.3 } }}
                      >
                        <motion.img
                          src={imgOf(tLeft, "/8.jpg")}
                          alt={tLeft?.title || "Trending"}
                          whileHover={{ scale: 1.05, transition: { duration: 0.3 } }}
                        />
                        <div className="trending-small-content">
                          <Link to={`/article/${tLeft?.slug}`} style={{ textDecoration: 'none' }}>
                            <span className="tag news">NEWS</span>
                          </Link>
                          <Link to={`/article/${tLeft?.slug}`} style={{ textDecoration: 'none' }}>
                            <motion.h4
                              whileHover={{
                                textDecoration: "underline",
                                color: "#c20a7c",
                                transition: { duration: 0.3 }
                              }}
                            >
                              {tLeft?.title || "—"}
                            </motion.h4>
                          </Link>
                        </div>
                      </motion.div>
                    ) : null}

                    {tLeftSide ? (
                      <motion.div
                        className="trending-small-card"
                        variants={miniNewsCardVariants}
                        whileHover={{ y: -5, scale: 1.02, transition: { duration: 0.3 } }}
                      >
                        <motion.img
                          src={imgOf(tLeftSide, "/7.jpg")}
                          alt={tLeftSide?.title || "Trending"}
                          whileHover={{ scale: 1.05, transition: { duration: 0.3 } }}
                        />
                        <div className="trending-small-content">
                          <Link to={`/article/${tLeftSide?.slug}`} style={{ textDecoration: 'none' }}>
                            <span className="tag news">NEWS</span>
                          </Link>
                          <Link to={`/article/${tLeftSide?.slug}`} style={{ textDecoration: 'none' }}>
                            <motion.h4
                              whileHover={{
                                textDecoration: "underline",
                                color: "#00d6c6",
                                transition: { duration: 0.3 }
                              }}
                            >
                              {tLeftSide?.title || "—"}
                            </motion.h4>
                          </Link>
                        </div>
                      </motion.div>
                    ) : null}
                  </motion.div>
                </motion.div>

                {/* RIGHT COLUMN */}
                <motion.div className="trending-right-col" variants={sidebarVariants}>
                  {tRight ? (
                    <motion.div
                      className="trending-tall-card"
                      variants={featuredArticleVariants}
                      whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
                    >
                      <motion.img
                        src={imgOf(tRight, "/6.jpg")}
                        alt={tRight?.title || "Trending"}
                        variants={featuredContentVariants}
                        whileHover={{ scale: 1.05, transition: { duration: 0.3 } }}
                      />
                      <motion.div className="trending-tall-overlay" variants={headingVariants}>
                        <Link to={`/article/${tRight?.slug}`} style={{ textDecoration: 'none' }}>
                          <span className="tag news">NEWS</span>
                        </Link>
                        <Link to={`/article/${tRight?.slug}`} style={{ textDecoration: 'none' }}>
                          <motion.h3
                            whileHover={{
                              textDecoration: "underline",
                              color: "#c20a7c",
                              transition: { duration: 0.3 }
                            }}
                          >
                            {tRight?.title || "—"}
                          </motion.h3>
                        </Link>
                        {/* <p className="supporting">...</p> */}
                      </motion.div>
                    </motion.div>
                  ) : null}
                </motion.div>
              </motion.div>
            </motion.section>
          )}

          {/* TOP PICKS SECTION */}
          <section className="section-block mb-5">
            <div className="toppicks-grid">

              {/* Card 1: TECH (from techNews) */}
              {techNews.length > 0 && (() => {
                const main = techNews[0];
                const supports = techNews.slice(1, 3);
                const tagText = (main?.category?.name || "Tech").toUpperCase();
                const tagClass = tagClassFor(main?.category?.name || "Tech");
                return (
                  <div className="toppick-card tech">
                    <Link to={`/article/${main?.slug}`} style={{ textDecoration: 'none' }}>
                      <span className={`tag ${tagClass}`}>{tagText}</span>
                    </Link>
                    <Link to={`/article/${main?.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <h3>{main?.title || "—"}</h3>
                    </Link>
                    <img className="toppick-img" src={imgOr(main, "/9.jpg")} alt={main?.title || "Tech"} />
                    <div className="toppick-support">
                      {supports.map((s) => (
                        <React.Fragment key={s.id}>
                          <Link to={hrefOf(s)} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <div className="toppick-support-title">{s?.title}</div>
                          </Link>
                          <div className="toppick-support-meta">
                            {timeAgo(s?.createdAt)}{s?.user?.name ? ` | ${s.user.name}` : ""}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Card 2: SHOPPING (from shoppingNews) */}
              {shoppingNews.length > 0 && (() => {
                const main = shoppingNews[0];
                const supports = shoppingNews.slice(1, 3);
                const tagText = (main?.category?.name || "Shopping").toUpperCase();
                const tagClass = tagClassFor(main?.category?.name || "Shopping");
                return (
                  <div className="toppick-card shopping">
                    <Link to={`/article/${main?.slug}`} style={{ textDecoration: 'none' }}>
                      <span className={`tag ${tagClass}`}>{tagText}</span>
                    </Link>
                    <Link to={`/article/${main?.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <h3>{main?.title || "—"}</h3>
                    </Link>
                    <img className="toppick-img" src={imgOr(main, "/10.jpg")} alt={main?.title || "Shopping"} />
                    <div className="toppick-support">
                      {supports.map((s) => (
                        <React.Fragment key={s.id}>
                          <Link to={`/article/${s?.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <div className="toppick-support-title">{s?.title}</div>
                          </Link>
                          <div className="toppick-support-meta">
                            {timeAgo(s?.createdAt)}{s?.user?.name ? ` | ${s.user.name}` : ""}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Card 3: LIFESTYLE (from lifestyle) */}
              {lifestyleNews.length > 0 && (() => {
                const main = lifestyleNews[0];
                const supports = lifestyleNews.slice(1, 3);
                const tagText = (main?.category?.name || "Lifestyle").toUpperCase();
                const tagClass = tagClassFor(main?.category?.name || "Lifestyle");
                return (
                  <div className="toppick-card lifestyle">
                    <Link to={`/article/${main?.slug}`} style={{ textDecoration: 'none' }}>
                      <span className={`tag ${tagClass}`}>{tagText}</span>
                    </Link>
                    <Link to={`/article/${main?.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <h3>{main?.title || "—"}</h3>
                    </Link>
                    <img className="toppick-img" src={imgOr(main, "/11.jpg")} alt={main?.title || "Lifestyle"} />
                    <div className="toppick-support">
                      {supports.map((s) => (
                        <React.Fragment key={s.id}>
                          <Link to={hrefOf(s)} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <div className="toppick-support-title">{s?.title}</div>
                          </Link>
                          <div className="toppick-support-meta">
                            {timeAgo(s?.createdAt)}{s?.user?.name ? ` | ${s.user.name}` : ""}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Optional: empty state if any group missing */}
              {(!techNews.length || !shoppingNews.length || !lifestyleNews.length) && (
                <div className="p-3 text-muted" style={{ gridColumn: "1 / -1" }}>
                  {/* You can remove this note, or keep for debugging */}
                  {/* Some cards are hidden because their data arrays were empty. */}
                </div>
              )}
            </div>
          </section>

          {/* Lunatic Fringe Banner */}
          <div ref={lunaticBannerRef} className="lunatic-banner" style={{ backgroundImage: "url('/12.jpg')" }}>
            <div ref={lunaticOverlayRef} className="lunatic-banner-overlay lunatic-banner-row">
              <div className="lunatic-banner-content-col">
                <div ref={lunaticLogoRef} className="lunatic-logo">If</div>
                <div ref={lunaticTitleRef} className="lunatic-title">Lunatic Fringe</div>
                <div ref={lunaticDescRef} className="lunatic-desc">A sanctuary of beauty and artistry, where only you matter. Here, every experience is personal—from the way we greet you at our door to how our skilled stylists and technicians listen intently to understand exactly what you want.</div>
              </div>
              <button ref={lunaticBtnRef} className="lunatic-btn">BOOK NOW</button>
            </div>
          </div>


          {/* FEATURED PICKS SECTION */}




          {/* Remove the featured picks section from Home.js */}

          {/* DUBAI NEWS SECTION */}
          <FeaturedAccordion data={homeRes?.data?.topPicks} />
          <section className="section-block dubai-news-section">
            <div className="section-title trending">Dubai News</div>

            {homeLoading ? (
              <div className="p-3 text-muted">Loading…</div>
            ) : dubaiNews.length === 0 ? (
              <div className="p-3 text-muted">No Dubai news available.</div>
            ) : (
              <div className="trending-two-col-row">

                {/* Left COLUMN (Tall card) */}
                <div className="trending-right-col">
                  {dTall && (
                    <div className="news-tall-card trending-tall-card">
                      <img src={dTall.thumbnail || "/17.jpg"} alt={dTall.title || "Dubai News"} />
                      <div className="trending-tall-overlay-2">
                        <Link to={`/article/${dTall?.slug}`} style={{ textDecoration: 'none' }}>
                          <span className={`tag ${tagClassFor(dTall.category?.name)}`}>
                            {(dTall.category?.name || "News").toUpperCase()}
                          </span>
                        </Link>
                        <Link to={`/article/${dTall?.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                          <h3>{dTall.title || "—"}</h3>
                        </Link>
                        {/* Optional supporting/summary text if you add it later */}
                        <p className="supporting">
                          {excerptFromHtml(dTall?.content, 50)}
                        </p>

                      </div>
                    </div>
                  )}
                </div>

                {/* Right COLUMN */}
                <div className="trending-left-col">
                  {/* Top large card */}
                  {dMain && (
                    <div className="dubai-large-card">
                      <div className="trending-large-img">
                        <img src={dMain.thumbnail || "/16.jpg"} alt={dMain.title || "Dubai News"} />
                      </div>
                      <div className="trending-large-content">
                        <Link to={`/article/${dMain?.slug}`} style={{ textDecoration: 'none' }}>
                          <span className={`tag ${tagClassFor(dMain.category?.name)}`}>
                            {(dMain.category?.name || "News").toUpperCase()}
                          </span>
                        </Link>
                        <Link to={`/article/${dMain?.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                          <h3>{dMain.title || "—"}</h3>
                        </Link>
                        <p className="supporting">
                          {excerptFromHtml(dMain?.content, 30)}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Bottom row: two small cards */}
                  <div className="trending-bottom-row-with-border">
                    {dSmall1 && (
                      <div className="trending-small-card">
                        <img src={dSmall1.thumbnail || "/18.jpg"} alt={dSmall1.title || "Dubai News"} />
                        <div className="trending-small-content">
                          <Link to={`/article/${dSmall1?.slug}`} style={{ textDecoration: 'none' }}>
                            <span className={`tag ${tagClassFor(dSmall1.category?.name)}`}>
                              {(dSmall1.category?.name || "News").toUpperCase()}
                            </span>
                          </Link>
                          <Link to={`/article/${dSmall1?.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <h4>{dSmall1.title || "—"}</h4>
                          </Link>
                        </div>
                      </div>
                    )}

                    {dSmall2 && (
                      <div className="trending-small-card">
                        <img src={dSmall2.thumbnail || "/19.jpg"} alt={dSmall2.title || "Dubai News"} />
                        <div className="trending-small-content">
                          <Link to={`/article/${dSmall2?.slug}`} style={{ textDecoration: 'none' }}>
                            <span className={`tag ${tagClassFor(dSmall2.category?.name)}`}>
                              {(dSmall2.category?.name || "News").toUpperCase()}
                            </span>
                          </Link>
                          <Link to={`/article/${dSmall2?.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <h4>{dSmall2.title || "—"}</h4>
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            )}
          </section>



          {/* Banner Ad */}
          <div ref={bannerAd1Ref} className="banner-ad-1" style={{ backgroundImage: `url('${secondAdbannerBg}')`, }}>
            <span ref={bannerText1Ref} className="banner-ad-text">{secondAdbannerText}</span>
            <span ref={bannerBtn1Ref} className="ad-btn">SHOP NOW</span>
          </div>


          {/* QUICK READ SECTION */}
          <section className="quickread-section exact-quickread">
            <div className="section-title quickread">QUICK READ</div>

            {homeLoading ? (
              <div className="p-3 text-muted">Loading…</div>
            ) : quikread.length === 0 ? (
              <div className="p-3 text-muted">No items yet.</div>
            ) : (
              <div className="quickread-list exact-quickread-list">
                {quikread.map((item) => (
                  <div key={item.id} className="quickread-card exact-quickread-card">
                    <img
                      className="quickread-img exact-quickread-img  "
                      src={item.thumbnail}
                      alt={item.title}
                    />
                    <div className="quickread-content exact-quickread-content">
                      <Link to={`/article/${item?.slug}`} style={{ textDecoration: "none" }}>
                        <span className={`tag ${item.tagClass} exact-quickread-tag`}>
                          {item.category?.name}
                        </span>
                      </Link>
                      <div className="exact-quickread-body">
                        <Link
                          to={`/article/${item.slug}`}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <div className="quickread-headline exact-quickread-headline">
                            {item.title}
                          </div>
                        </Link>
                        <div className="quickread-support exact-quickread-support">
                          {excerptFromHtml(item.content, 50)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* <div className="quickread-loader">
              <div className="spinner"></div>
            </div> */}
          </section>







        </main>

      </div>
    </>
  );
}